import React from "react";
import { Document, Page, View, Text, Image } from "@react-pdf/renderer";
import { styles, elab, statusColour } from "./pdfTheme";
import { thumbFor } from "./photos";
import { Cover, CoverDetails, Chrome, Heading, Narrative, ScoreAnalysis, OverallAssessment, Scope } from "./pdfSections";

/**
 * The Elaborate layout — each finding set out in full. Ported from
 * Csa/report/CsaElaboratePdf.js.
 *
 * The same report as Standard, at a different level of detail. Standard is a
 * register: one row per finding, ~7 to a page, for long reports and clients
 * who want the list. Elaborate gives each finding its own block, ~4 to a
 * page, for short reports and findings that need explaining.
 *
 * Everything except the findings section is identical between the two
 * layouts and lives in pdfSections.js, so a client comparing a Standard and
 * an Elaborate report of the same audit cannot find different numbers in
 * them.
 */

/* ------------------------------------------------------------------ */
/* one finding                                                         */
/* ------------------------------------------------------------------ */

/**
 * One finding, as a ruled table. Every row is label-then-value in the same
 * two columns, so an eye running down forty findings looking for "Action"
 * finds it in the same place every time. Rows appear only when they carry
 * something — a conformant finding has no Action.
 *
 * wrap={false} keeps a finding whole: half a table at the foot of a page,
 * with its Action overleaf, is how a reader ends up attributing one
 * finding's remedy to another.
 */
function Finding({ f }) {
  const rows = [];
  if (f.statement) rows.push(["Finding", f.statement]);
  if (f.note) rows.push(["Observed", f.note]);
  if (f.recommendation) rows.push(["Action", f.recommendation]);
  if (f.legal_reference) rows.push(["Legal reference", f.legal_reference]);

  const photos = (f.photos || []).slice(0, 6);
  const lastTextRow = photos.length ? -1 : rows.length - 1;

  return (
    <View style={elab.tbl} wrap={false}>
      <View style={elab.tHead}>
        <Text style={elab.tNumber}>{f.number}</Text>
        <Text style={[elab.tStatus, { color: statusColour(f.status) }]}>
          {f.status_label}
        </Text>
      </View>

      {rows.map(([label, value], i) => (
        <View key={label} style={i === lastTextRow ? elab.tRowLast : elab.tRow}>
          <Text style={elab.tLabel}>{label}</Text>
          <Text style={elab.tValue}>{value}</Text>
        </View>
      ))}

      {photos.length ? (
        <View style={elab.tRowLast}>
          <Text style={elab.tLabel}>Evidence</Text>
          <View style={elab.tPhotos}>
            {photos.map((u, k) => (
              <Image key={k} src={thumbFor(u)} style={elab.photo} />
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* the findings section                                                */
/* ------------------------------------------------------------------ */

function Findings({ section, header }) {
  const d = section.data;

  return (
    <Page size="A4" style={styles.page}>
      <Chrome header={header} />
      <Heading no={section.no} title={section.title} />
      <Text style={[styles.muted, { marginBottom: 10 }]}>
        {d.selected} finding{d.selected === 1 ? "" : "s"} shown, from {d.assessed} checkpoints
        assessed.
      </Text>

      {!d.areas.length ? (
        <Text style={styles.empty}>No findings were selected for this report.</Text>
      ) : null}

      {d.areas.map((a) => (
        <View key={a.name}>
          <Text style={elab.area}>{a.name}</Text>
          {a.categories.map((c) => (
            <View key={c.name}>
              <Text style={elab.category}>{c.name}</Text>
              {c.findings.map((f) => (
                <Finding key={f.number} f={f} />
              ))}
            </View>
          ))}
        </View>
      ))}
    </Page>
  );
}

/* ------------------------------------------------------------------ */

export default function ElectricalElaboratePdf({ model }) {
  const { header, summary, sections } = model;
  const byKey = (k) => sections.find((s) => s.key === k);

  const renderSection = (s) => {
    if (s.key === "score_analysis") return <ScoreAnalysis key={s.key} section={s} />;
    if (s.key === "overall_assessment") return <OverallAssessment key={s.key} section={s} />;
    if (s.key === "scope") return <Scope key={s.key} section={s} />;
    return <Narrative key={s.key} section={s} />;
  };

  const before = sections.filter((s) => s.no < 7);
  const after = sections.filter((s) => s.no > 7);

  return (
    <Document
      title={`Electrical — ${header.organization} — ${header.site}`}
      author={header.prepared_by || "Momentum"}
      subject="Electrical Audit"
    >
      <Cover header={header} summary={summary} />

      <Page size="A4" style={styles.page}>
        <Chrome header={header} />
        <CoverDetails header={header} summary={summary} />
      </Page>

      {before.map((s) => (
        <Page key={s.key} size="A4" style={styles.page}>
          <Chrome header={header} />
          {renderSection(s)}
        </Page>
      ))}

      {/*
        Portrait, unlike Standard. Elaborate has no eight-column table to
        fit, so there is nothing to turn the page for — and prose reads
        better in portrait, which is the whole reason to pick this layout.
      */}
      <Findings section={byKey("findings")} header={header} />

      {after.map((s) => (
        <Page key={s.key} size="A4" style={styles.page}>
          <Chrome header={header} />
          {renderSection(s)}
        </Page>
      ))}
    </Document>
  );
}
